/**
 * ч.843 (рішення власника): реєстр віджетів головної + налаштування набору.
 *
 * Користувач додає/прибирає віджети; вибір — у ПРОФІЛІ (uiPrefs, ключ
 * `dash-widgets:v1`), тобто переживає зміну браузера/компʼютера.
 */
import type { ReactNode } from 'react';
import {
  BankFlowWidget, SavedPaymentsWidget, UnpostedWidget, UnrecognizedWidget, UnsignedWidget,
} from './widgets/MonitorWidgets';
import { BirthdaysWidget, DebtWidget, TaxCalendarWidget } from './widgets/InfoWidgets';
import FopLimitsWidget from './widgets/FopLimitsWidget';
import FinanceWidget from './widgets/FinanceWidget';

export interface WidgetDef {
  id: string;
  label: string;
  /** ч.1311: право, без якого віджет не показується і не запитує сервер (інакше — 403 на кожне відкриття головної). */
  permission?: string;
  render: () => ReactNode;
}

export const WIDGETS: WidgetDef[] = [
  // ч.1364: виручка (і план), прибуток, гроші — те саме зведення, що сторінка «Фінанси».
  { id: 'finance', label: 'Фінанси місяця', permission: 'reports.management.view',
    render: () => <FinanceWidget /> },
  { id: 'unposted', label: 'Непроведені', permission: 'accounting.view',
    render: () => <UnpostedWidget /> },
  { id: 'unsigned', label: 'Непідписані', permission: 'documents.attachments.manage',
    render: () => <UnsignedWidget /> },
  { id: 'bank_in', label: 'Надходження на рахунок', permission: 'bank.view',
    render: () => <BankFlowWidget kind="receipts" /> },
  { id: 'bank_out', label: 'Списання з рахунку', permission: 'bank.view',
    render: () => <BankFlowWidget kind="payments" /> },
  { id: 'saved_payments', label: 'Збережені платіжки', permission: 'bank.view',
    render: () => <SavedPaymentsWidget /> },
  { id: 'unrecognized_in', label: 'Нерозпізнані надходження', permission: 'bank.view',
    render: () => <UnrecognizedWidget kind="receipts" /> },
  { id: 'unrecognized_out', label: 'Нерозпізнані списання', permission: 'bank.view',
    render: () => <UnrecognizedWidget kind="payments" /> },
  { id: 'debt_recv', label: 'Борг дебет', permission: 'accounting.view',
    render: () => <DebtWidget kind="receivable" /> },
  { id: 'debt_pay', label: 'Борг кредит', permission: 'accounting.view',
    render: () => <DebtWidget kind="payable" /> },
  { id: 'birthdays', label: 'Дні народження', permission: 'accounting.view',
    render: () => <BirthdaysWidget /> },
  { id: 'tax_calendar', label: 'Податковий календар', permission: 'accounting.view',
    render: () => <TaxCalendarWidget /> },
  { id: 'fop_limits', label: 'Ліміти єдинників', permission: 'refs.fop_reports.view',
    render: () => <FopLimitsWidget /> },
];

/** Набір за замовчуванням — усі, крім банківських (щоб не перевантажувати). */
export const DEFAULT_WIDGET_IDS = [
  'finance', 'unposted', 'unsigned', 'saved_payments', 'unrecognized_in', 'unrecognized_out',
  'tax_calendar', 'debt_recv', 'debt_pay', 'birthdays', 'fop_limits',
];

export const WIDGETS_PREF_KEY = 'dash-widgets:v1';

/** ч.1364: віджети, що зʼявились ПІСЛЯ налаштування наборів (ч.843). Користувачу зі збереженим
 * набором такий віджет додається сам — доки він не прибере його у «налаштувати віджети»
 * (прибрані зберігаються окремо, `WIDGETS_HIDDEN_KEY`). */
export const AUTO_ADD_WIDGET_IDS = ['finance'];
export const WIDGETS_HIDDEN_KEY = 'dash-widgets:hidden';

/** Набір до показу: збережений (або типовий) + нові віджети, яких користувач ще не прибирав. */
export function effectiveWidgetIds(stored: string[] | null | undefined, hidden: string[] | null | undefined): string[] {
  if (!stored) return DEFAULT_WIDGET_IDS;
  const fresh = AUTO_ADD_WIDGET_IDS.filter((id) => !stored.includes(id) && !(hidden ?? []).includes(id));
  return [...fresh, ...stored];
}
